import type { SearchFilters } from '@content-search/domain';
import { hasActiveFilters } from '@content-search/utils';
import {
  Card,
  Button,
  IconButton,
  ButtonSize,
  ButtonType,
  IconSize,
  OverlayMode,
  IconButtonSize,
  TextInput,
  Search,
  Filter,
  ArrowUp,
} from '@soteria/design-system-ve';
import { useCallback, useState, useEffect } from 'react';

import { SearchFilterBadges } from './SearchFilterBadges';
import * as styles from './SearchInput.css';

interface SearchInputProps {
  searchFilters: SearchFilters;
  updateSearchFilters: (updates: Partial<SearchFilters>) => void;
  isLoading: boolean;
  isFiltersPanelOpen: boolean;
  onSearch: (filters: SearchFilters) => void;
  toggleFiltersPanel?: () => void;
  isStickyPanel?: boolean;
}

export function SearchInput({
  searchFilters,
  updateSearchFilters,
  isLoading,
  isFiltersPanelOpen,
  onSearch,
  toggleFiltersPanel,
  isStickyPanel,
}: SearchInputProps) {
  const [userId, setUserId] = useState(searchFilters.userId || '');
  const [subjectId, setSubjectId] = useState(searchFilters.subjectId || '');

  // Sync local state when searchFilters change from external sources
  useEffect(() => {
    setUserId(searchFilters.userId || '');
    setSubjectId(searchFilters.subjectId || '');
  }, [searchFilters.userId, searchFilters.subjectId]);

  const handleUserIdChange = useCallback((value: string) => {
    setUserId(value);
  }, []);

  const handleSubjectIdChange = useCallback((value: string) => {
    setSubjectId(value);
  }, []);

  function handleScrollToTop() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  const handleSearch = useCallback(() => {
    // Update search filters and trigger search with current local values
    const updatedFilters = {
      ...searchFilters,
      userId: userId || undefined,
      subjectId: subjectId || undefined,
    };

    // Update URL with current local values
    updateSearchFilters({
      userId: userId || undefined,
      subjectId: subjectId || undefined,
    });

    // Trigger search with updated filters
    onSearch(updatedFilters);
  }, [userId, subjectId, searchFilters, updateSearchFilters, onSearch]);

  const activeFilters = hasActiveFilters();

  return (
    <Card backgroundColor={undefined} className={isStickyPanel ? styles.stickySearchOptionsCard : styles.searchOptionsCard}>
      <div className={styles.searchContainer}>
        {/* User ID Text Input */}
        <div className={styles.searchField}>
          <label htmlFor="userId">User ID</label>
          <TextInput disabled={isLoading} id="userId" placeholder="Search by User ID" prefixIcon={Search} value={userId} onChange={handleUserIdChange} />
        </div>

        {/* Subject ID Text Input */}
        <div className={styles.searchField}>
          <label htmlFor="subjectId">Subject ID</label>
          <TextInput
            disabled={isLoading}
            id="subjectId"
            placeholder="Search by Subject ID"
            prefixIcon={Search}
            value={subjectId}
            onChange={handleSubjectIdChange}
          />
        </div>

        {/* Search Button */}
        <Button
          buttonSize={ButtonSize.Default}
          buttonType={ButtonType.Brand}
          className={styles.searchButton}
          disabled={isLoading}
          label="SEARCH"
          onClick={handleSearch}
        />

        {/* Action Button - Scroll to Top or Filters Toggle */}
        {isStickyPanel ? (
          <IconButton
            aria-label="Scroll to top"
            buttonSize={IconButtonSize.Medium}
            buttonType={ButtonType.Primary}
            icon={ArrowUp}
            iconSize={IconSize.XSmall}
            overlayMode={OverlayMode.Solid}
            title="Scroll to top"
            onClick={handleScrollToTop}
          />
        ) : (
          <IconButton
            aria-label={isFiltersPanelOpen ? 'Hide filters' : 'Show filters'}
            buttonSize={IconButtonSize.Medium}
            buttonType={ButtonType.Primary}
            className={styles.filterButton}
            data-active={isFiltersPanelOpen}
            data-has-filters={activeFilters}
            disabled={isLoading}
            icon={Filter}
            iconSize={IconSize.XSmall}
            overlayMode={OverlayMode.Solid}
            title={`${isFiltersPanelOpen ? 'Hide' : 'Show'} filters${activeFilters ? ' (active)' : ''}`}
            onClick={toggleFiltersPanel}
          />
        )}

        {/* Filter Badges Section */}
        <div className={styles.filterBadgesArea}>
          <SearchFilterBadges isLoading={isLoading} searchFilters={searchFilters} onRemoveFilter={updateSearchFilters} />
        </div>
      </div>
    </Card>
  );
}
